import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  pickBulkActions,
  runBulkAction,
  type BulkActionCandidate,
  type BulkActionRunResult,
} from './bulkActionRunner';
import type { RowAction } from './types';

interface BulkActionMenuProps<TData> {
  actions?: RowAction<TData>[] | ((data: TData) => RowAction<TData>[]);
  rows: TData[];
  /** Called after a successful run so the caller can clear selection. */
  onRunComplete?: (result: BulkActionRunResult) => void;
}

/**
 * Ellipsis dropdown that exposes bulk-capable actions for the current
 * selection.
 *
 * UX rules:
 * - Destructive actions (`variant === 'destructive'`) require confirmation via
 *   a minimal {@link AlertDialog} naming the action + count.
 * - Non-destructive actions fire immediately on click.
 *
 * In-flight state is rendered inline on the trigger button — a spinner plus a
 * brief label (with per-row counter for the `sequential` tier; just a spinner
 * for `bulk`, which is a single roundtrip).
 */
export function BulkActionMenu<TData>({
  actions,
  rows,
  onRunComplete,
}: BulkActionMenuProps<TData>) {
  const [pendingCandidate, setPendingCandidate] =
    useState<BulkActionCandidate<TData> | null>(null);
  const [runningCandidate, setRunningCandidate] =
    useState<BulkActionCandidate<TData> | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });

  const availableCandidates = useMemo(() => {
    if (!actions) return [];
    return pickBulkActions(actions, rows);
  }, [actions, rows]);

  const executeCandidate = useCallback(
    async (candidate: BulkActionCandidate<TData>) => {
      const { action, applicableRows } = candidate;
      setRunningCandidate(candidate);
      setProgress({ done: 0, total: applicableRows.length });
      const isBulkTier = action.bulk?.tier === 'bulk';
      try {
        const result = await runBulkAction(action, applicableRows, {
          onProgress: (done, total) => setProgress({ done, total }),
        });
        if (result.failed.length === 0) {
          // `tier: 'bulk'` handlers own their own success toast. For
          // sequential, emit a generic one so users always get feedback.
          if (action.bulk?.tier === 'sequential') {
            toast.success(`${result.succeeded} items updated`);
          }
        } else {
          toast.error(
            `${result.succeeded} succeeded, ${result.failed.length} failed`,
            {
              description: result.failed
                .slice(0, 3)
                .map((f) => f.error.message)
                .join('\n'),
            },
          );
        }
        onRunComplete?.(result);
      } catch (err) {
        // For `tier: 'bulk'` the handler owns its error toast — don't
        // double-toast. For any other unexpected throw, surface it generically.
        if (!isBulkTier) {
          const message = err instanceof Error ? err.message : String(err);
          toast.error('Bulk action failed', { description: message });
        }
      } finally {
        setRunningCandidate(null);
        setPendingCandidate(null);
      }
    },
    [onRunComplete],
  );

  const handleSelect = useCallback(
    (candidate: BulkActionCandidate<TData>) => {
      const needsConfirm =
        candidate.action.variant === 'destructive' ||
        candidate.action.confirmInBulk === true;
      if (needsConfirm) {
        setPendingCandidate(candidate);
      } else {
        void executeCandidate(candidate);
      }
    },
    [executeCandidate],
  );

  const handleConfirm = useCallback(() => {
    if (!pendingCandidate) return;
    void executeCandidate(pendingCandidate);
  }, [pendingCandidate, executeCandidate]);

  if (availableCandidates.length === 0) {
    return null;
  }

  const running = runningCandidate !== null;
  const runningAction = runningCandidate?.action ?? null;
  const runningLabel =
    runningAction && runningCandidate
      ? labelOf(runningAction, runningCandidate.applicableRows[0])
      : '';
  const runningCount = runningCandidate?.applicableRows.length ?? 0;
  const isRunningSequential = runningAction?.bulk?.tier === 'sequential';

  const pendingAction = pendingCandidate?.action ?? null;
  const pendingApplicable = pendingCandidate?.applicableRows ?? [];
  const pendingLabel =
    pendingAction && pendingApplicable[0]
      ? labelOf(pendingAction, pendingApplicable[0])
      : '';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="More bulk actions"
            disabled={running}
          >
            {running ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                <span className="hidden sm:inline">
                  {runningLabel} {runningCount} item
                  {runningCount !== 1 ? 's' : ''}
                  {isRunningSequential
                    ? ` (${progress.done}/${progress.total})`
                    : '…'}
                </span>
              </>
            ) : (
              <MoreHorizontal className="size-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top">
          {availableCandidates.map((candidate, idx) => {
            const Icon = candidate.action.icon;
            return (
              <DropdownMenuItem
                key={idx}
                variant={candidate.action.variant}
                onSelect={() => handleSelect(candidate)}
              >
                <Icon className="size-4" />
                {labelOf(candidate.action, candidate.applicableRows[0])}
                {candidate.applicableRows.length !== rows.length && (
                  <span className="text-muted-foreground ml-auto text-xs">
                    {candidate.applicableRows.length}/{rows.length}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={pendingCandidate !== null}
        onOpenChange={(open: boolean) => {
          if (!open && !running) setPendingCandidate(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingLabel} {pendingApplicable.length} row
              {pendingApplicable.length !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to perform {pendingLabel.toLowerCase()} on{' '}
              {pendingApplicable.length} row
              {pendingApplicable.length !== 1 ? 's' : ''}?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={running}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={
                pendingAction?.variant === 'destructive'
                  ? 'destructive'
                  : 'default'
              }
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={running}
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function labelOf<TData>(action: RowAction<TData>, row: TData): string {
  return typeof action.label === 'function' ? action.label(row) : action.label;
}
